import { Stack } from '@mui/material';
import {
  ResourceType,
  type InventoryResource,
  type Provider,
} from '@repo/api-gw-sdk';
import React from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { useViewParameters } from '@/contexts/navigation';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { CloudProviders } from '@/data/inventory/data';

import { Panels } from '../panels/panels';
import { OtherSnapshotsMultipleDots } from '../snapshots/otherSnapshotsMultipleDots';

interface ConvertToEonSnapshotsBarProps {
  numOfOtherSnapshots: number;
  cloudProvider: Provider;
  resource: InventoryResource;
}

export function ConvertToEonSnapshotsBar(props: ConvertToEonSnapshotsBarProps) {
  const { rightPanel } = useWorkspace();
  const { numOfOtherSnapshots, cloudProvider, resource } = props;
  const { isAuthorizedResource } = useRoles();
  const { removeParam } = useViewParameters();
  const { AzureSnapshotsDiscoverySupport } = useFeatureFlags();

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resource.id,
        onClose,
        initialTab: 'eon-snapshots',
      },
    });
  };

  return (
    (resource.resourceType === ResourceType.AwsEc2 ||
      (resource.resourceType === ResourceType.AzureVirtualMachine &&
        AzureSnapshotsDiscoverySupport)) &&
    !!numOfOtherSnapshots && (
      <Stack
        alignItems='center'
        justifyContent='space-between'
        direction='row'
        paddingX='40px'
        paddingY='16px'
        className='bg-(--mui-palette-warning-opacity)'
        sx={{
          boxShadow: 'var(--mui-customShadows-lg)',
        }}
      >
        <Stack direction='row' alignItems='center' gap='12px'>
          <OtherSnapshotsMultipleDots />
          {`${numOfOtherSnapshots} ${CloudProviders[cloudProvider]?.displayName} snapshots in the source account can be converted.`}
        </Stack>
        <ButtonWithIcon
          variant='outlined'
          icon='material-symbols-join-right-rounded'
          text={'Convert to Eon'}
          isAuthorized={isAuthorizedResource('create:jobs', resource)}
          onClick={() => {
            rightPanel.setComponent({
              panel: Panels.ConvertToMultipleEonSnapshots,
              props: {
                onClose,
                onBack,
                resource,
              },
            });
            rightPanel.setIsOpen(true);
          }}
        />
      </Stack>
    )
  );
}
